// THE THREE STATES OF THE TRACKING SCOPE, away from the rendering.
//
// Owner, 2026-09-25: keep the autosave AND add the button - "for human
// behaviour". That separates two things that had been conflated: THE
// DATA SAVE IS CHEAP AND AUTOMATIC, THE VIEW TRANSITION IS EXPENSIVE
// AND EXPLICIT. Ticks are persisted immediately so nothing is ever
// lost, and the viewer still rebuilds once rather than once per tick.
//
// SO "SAVED" IS NOT "APPLIED", AND THE MIDDLE STATE NEEDS ITS OWN
// WORDS. A button reading "Saved" while the stage still shows the old
// scope tells a person the screen matches their ticks when it does not
// - a control reporting success for something that has not happened.
// And the suggested "Unsaved changes" would be false in the other
// direction: with autosave on, the changes ARE saved. What is pending
// is the VIEW.
export const APPLY_STATES = {
  applied: {
    key: "applied",
    label: "Applied to the view",
    button: "",                       // nothing to press
    hint: "",
  },
  pending: {
    key: "pending",
    label: "Saved, not applied to the view yet",
    button: "Apply to view",
    hint: "Your ticks are saved. The model on screen still shows the previous selection.",
  },
  applying: {
    key: "applying",
    label: "Applying...",
    button: "Applying...",
    hint: "Rebuilding the model for the new selection.",
  },
  // NOTHING DREW, SO NOTHING CAN ACKNOWLEDGE (Paul, 2026-09-25).
  // `applied` is written by the loader's admission check, so it can only
  // arrive from a loader that actually runs - and on a project with no
  // published model nothing is ever normalised and nothing is ever
  // consulted. "Applying..." would then spin for ever.
  //
  // THE ANSWER IS NOT A TIMEOUT. A timer that gave up would hide a
  // genuine stall, which is the class we have spent two days removing,
  // and announcing "applied" on a project where nothing was filtered
  // would be a guard whose failure looks like working. The honest state
  // is that there is nothing to apply it TO, and that is true rather
  // than pending.
  nothingToApply: {
    key: "nothingToApply",
    label: "Saved. There is no model on screen to apply it to.",
    button: "",
    hint: "Your ticks are saved and will apply when this project has a published model.",
  },
};

/**
 * `savedNonce`   bumps every time the scope is persisted.
 * `appliedNonce` is the nonce the answer ON SCREEN belongs to.
 * `busy`         the hook has been asked and has not re-opened its gate.
 */
export function applyState({ savedNonce = 0, appliedNonce = 0, busy = false, hasModel = true } = {}) {
  // Checked BEFORE busy: with no model the acknowledgement can never
  // come, so "applying" would be a promise nothing can keep.
  if (!hasModel) return savedNonce === appliedNonce ? APPLY_STATES.applied : APPLY_STATES.nothingToApply;
  if (busy) return APPLY_STATES.applying;
  return savedNonce === appliedNonce ? APPLY_STATES.applied : APPLY_STATES.pending;
}

/**
 * The sentence under the button, from the server's own preview.
 *
 * NAMED, NOT COUNTED, for the categories: "this empties Couplings (from
 * Elbows)" is something a person can act on; "1 category" is something
 * they can only worry about.
 */
export function previewWords(p) {
  if (!p) return { line: "", warning: "" };
  if (p.previewUnavailable) return { line: p.previewUnavailable, warning: "" };
  const leaving = Number(p.leaving) || 0;
  const sections = Number(p.sections) || 0;
  // WHAT ARRIVES, NOT ONLY WHAT LEAVES. The preview was written for the
  // destructive direction - it is unticking that needs a warning - and
  // it said "Nothing leaves the tracked selection." to somebody who had
  // just TICKED one. True, and an answer to a question they had not
  // asked: they wanted to know what their change does, and the honest
  // reply for an addition is what it adds.
  const before = Number(p.before);
  const after = Number(p.after);
  const arriving = Number.isFinite(before) && Number.isFinite(after) && after > before ? after - before : 0;
  if (!leaving && !sections) {
    return {
      line: arriving
        ? `Adds ${arriving.toLocaleString()} element${arriving === 1 ? "" : "s"}. Nothing leaves.`
        : "Nothing leaves the tracked selection.",
      warning: "",
    };
  }
  const bits = [];
  if (sections) bits.push(`${sections.toLocaleString()} sub-categor${sections === 1 ? "y" : "ies"}`);
  if (leaving) bits.push(`${leaving.toLocaleString()} element${leaving === 1 ? "" : "s"}`);
  const emptied = (p.emptiedCategories || []).filter(Boolean);
  return {
    line: `Removes ${bits.join(", ")}.`,
    // Only the ones that would actually lose their parts are named.
    warning: emptied.length
      ? `This also empties ${emptied.map((c) => `"${c.name}"`).join(", ")}.`
      : "",
  };
}


/**
 * Is there a model on screen for a scope to be applied TO?
 *
 * `stats` carries the live commit, or null when the project has none.
 * Kept out of the component because a bare `||` in that file's body is
 * one more code path through a hooks linter that already counts too
 * many.
 */
export const hasLiveModel = (stats) => !stats || !!stats.commitId;


/**
 * THE WHOLE STATE FROM RAW VALUES, in one call.
 *
 * Every branch this makes is a branch NOT taken inside ModelTracking,
 * whose hooks linter counts code paths through the entire component and
 * reports "React Hook called conditionally" at unrelated earlier hooks
 * once it overflows. Measured, not guessed: adding three ternaries and
 * a guard to that file turned 0 errors into 11.
 *
 * `appliedNonce` is the loader's own answer and is NULL across a
 * teardown, so "not yet known" must read as busy rather than as
 * applied - absent is not equal.
 */
export function scopeApplyFrom({ savedNonce = 0, applyNonce = 0, appliedNonce = null, stats = null } = {}) {
  const known = appliedNonce === null || appliedNonce === undefined ? null : appliedNonce;
  return applyState({
    savedNonce,
    appliedNonce: known === null ? -1 : known,
    busy: known === null || applyNonce !== known,
    hasModel: hasLiveModel(stats),
  });
}
